import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import SEOHead from '../components/SEOHead';
import { apiService } from '../services/api';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'standard'
  });
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [transmissionResult, setTransmissionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleBack = () => {
    navigate('/');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsTransmitting(true);
    setTransmissionResult(null);
    
    try {
      const response = await apiService.submitContact({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        priority: formData.priority as 'low' | 'standard' | 'high' | 'critical'
      });
      
      if (response.success) {
        setTransmissionResult({
          success: true,
          message: 'MESSAGE_TRANSMITTED_SUCCESSFULLY'
        });
        
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          priority: 'standard'
        });
      } else {
        setTransmissionResult({
          success: false,
          message: 'TRANSMISSION_FAILED - RETRY_RECOMMENDED'
        });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setTransmissionResult({
        success: false,
        message: 'NETWORK_ERROR - CHECK_CONNECTION'
      });
    } finally {
      setIsTransmitting(false);
    }
  };

  const contactMethods = [
    {
      protocol: "EMAIL_DIRECT",
      address: "muhamadbashirushdan@gmail.com",
      status: "ACTIVE",
      encryption: "TLS_1.3",
      response: "< 24h"
    },
    {
      protocol: "LINKEDIN_SECURE",
      address: "/in/muhamadbashirushdan",
      status: "ACTIVE", 
      encryption: "OAUTH_2.0",
      response: "< 48h"
    },
    {
      protocol: "GITHUB_REPO",
      address: "/bashirushdan",
      status: "MONITORING",
      encryption: "SSH_KEY",
      response: "Variable"
    },
    {
      protocol: "WHATSAPP_ENCRYPTED",
      address: "+60-CLASSIFIED",
      status: "STANDBY",
      encryption: "E2E",
      response: "Real-time"
    }
  ];

  const systemInfo = [
    { label: "Location", value: "Kelantan, Malaysia", status: "CONFIRMED" },
    { label: "Timezone", value: "UTC+8 (MYT)", status: "ACTIVE" },
    { label: "Availability", value: "Mon-Fri 9AM-6PM", status: "SCHEDULED" },
    { label: "Response Rate", value: "98.7%", status: "OPTIMAL" }
  ];

  return (
    <>
      <SEOHead
        title="Contact | B.a.s.H Neural Core - Get in Touch"
        description="Contact Dr. Muhamad Bashirushdan Azemi for medical consultations, development projects, or professional collaborations. Secure communication channels available."
        keywords="contact, medical consultation, development projects, malaysia, kelantan, plastic surgery consultation, web development"
        url="https://bash.my/contact"
        type="website"
      />
      <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-black"></div>
      </div>

      {/* Communication interface grid */}
      <div className="fixed inset-0 z-[2] pointer-events-none">
        <div className="absolute inset-0 bg-cyber-grid opacity-5" style={{ backgroundSize: '60px 60px' }}></div>
        {/* Interface corners */}
        <div className="absolute top-16 left-4 w-16 h-16 border-l-4 border-t-4 border-cyan-400/40"></div>
        <div className="absolute top-16 right-4 w-16 h-16 border-r-4 border-t-4 border-cyan-400/40"></div>
        <div className="absolute bottom-16 left-4 w-16 h-16 border-l-4 border-b-4 border-cyan-400/40"></div>
        <div className="absolute bottom-16 right-4 w-16 h-16 border-r-4 border-b-4 border-cyan-400/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-cyan-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ ./establish_communication.sh
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            COMMUNICATION_MODULE
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Secure Channels • Direct Protocol • Encrypted Transmission
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* System Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-cyan-900/20 to-cyan-700/10 border border-cyan-400/30 p-8 rounded-lg mb-8"
          >
            <h2 className="text-cyan-400 font-mono text-2xl mb-6">SYSTEM_STATUS</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {systemInfo.map((info, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl text-white font-bold mb-2">{info.value}</div>
                  <div className="text-gray-400 mb-1">{info.label}</div>
                  <div className="text-green-400 text-xs">{info.status}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Communication Protocols */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-black/60 border border-green-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <h3 className="text-green-400 font-mono text-xl">AVAILABLE_PROTOCOLS</h3>
              </div>
              
              <div className="space-y-4">
                {contactMethods.map((method, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + (i * 0.1) }}
                    className="border-l-2 border-green-400/50 pl-4 hover:border-green-400 transition-colors cursor-pointer group"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold group-hover:text-green-400 transition-colors">
                          {method.protocol}
                        </div>
                        <div className="text-gray-400 text-sm">{method.address}</div>
                      </div>
                      <div className="text-green-400 text-xs font-mono">
                        {method.status}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Encryption: </span>
                        <span className="text-cyan-400">{method.encryption}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Response: </span>
                        <span className="text-white">{method.response}</span>
                      </div>
                    </div>
                    
                    <motion.div
                      className="opacity-0 group-hover:opacity-100 transition-opacity mt-2"
                      whileHover={{ scale: 1.05 }}
                    >
                      <span className="text-green-400 text-xs cursor-pointer">
                        [INITIATE_CONTACT] &gt;&gt;
                      </span>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Message Transmission Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-black/60 border border-blue-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <h3 className="text-blue-400 font-mono text-xl">MESSAGE_TRANSMISSION</h3>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Sender ID</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-black/50 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-400 focus:outline-none transition-colors"
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Contact Protocol</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-black/50 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-400 focus:outline-none transition-colors"
                      placeholder="your.email@domain.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Message Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full bg-black/50 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-400 focus:outline-none transition-colors"
                      placeholder="Brief description"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Priority Level</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="w-full bg-black/50 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-400 focus:outline-none transition-colors"
                    >
                      <option value="low">LOW - General Inquiry</option>
                      <option value="standard">STANDARD - Business</option>
                      <option value="high">HIGH - Urgent</option>
                      <option value="critical">CRITICAL - Emergency</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Message Content</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full bg-black/50 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-400 focus:outline-none transition-colors resize-none"
                    placeholder="Compose your message here..."
                    required
                  ></textarea>
                </div>
                
                <motion.button
                  type="submit"
                  disabled={isTransmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-3 px-6 rounded font-mono text-sm transition-all duration-300 ${
                    isTransmitting
                      ? 'bg-yellow-400/20 border border-yellow-400 text-yellow-400 cursor-not-allowed'
                      : 'bg-blue-400/20 border border-blue-400 text-blue-400 hover:bg-blue-400/30'
                  }`}
                >
                  {isTransmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>TRANSMITTING...</span>
                    </div>
                  ) : (
                    '[TRANSMIT_MESSAGE] >>'
                  )}
                </motion.button>
              </form>
              
              {/* Transmission Result */}
              {transmissionResult && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 p-3 rounded border-l-2 ${
                    transmissionResult.success 
                      ? 'bg-green-400/10 border-green-400/50' 
                      : 'bg-red-400/10 border-red-400/50'
                  }`}
                >
                  <div className={`${
                    transmissionResult.success ? 'text-green-400' : 'text-red-400'
                  } text-xs font-mono`}>
                    &gt; {transmissionResult.message}<br/>
                    &gt; TIMESTAMP: {new Date().toISOString().split('T')[1].split('.')[0]}<br/>
                    &gt; PROTOCOL: {transmissionResult.success ? 'SUCCESS' : 'ERROR'}
                  </div>
                </motion.div>
              )}

              {/* Transmission Status */}
              <div className="mt-4 p-3 bg-black/30 rounded border-l-2 border-cyan-400/50">
                <div className="text-cyan-400 text-xs font-mono">
                  &gt; SECURE_CHANNEL: ACTIVE<br/>
                  &gt; ENCRYPTION: AES-256<br/>
                  &gt; STATUS: READY_FOR_TRANSMISSION
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Contact;